import { useContext, useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { InfluencerDataContext } from '../../../contexts/InfluencerDataContext';
import { influencerApi } from '../../../services/api/influencer';
import { shouldShowWalletPayoutIntro } from '../utils/walletPayoutIntroUtils';

interface UseWalletPayoutIntroOptions {
  /** When false, the intro modal stays closed (e.g. while editing a profile section). */
  enabled?: boolean;
  onSetup?: () => void | Promise<void>;
}

export function useWalletPayoutIntro(options: UseWalletPayoutIntroOptions = {}) {
  const { enabled = true, onSetup } = options;
  const { influencerData } = useContext(InfluencerDataContext);
  const [introDismissedLocally, setIntroDismissedLocally] = useState(false);
  const [isIntroSetupLoading, setIsIntroSetupLoading] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const shouldShowIntro =
    enabled && shouldShowWalletPayoutIntro(influencerData) && !introDismissedLocally;

  useEffect(() => {
    if (shouldShowIntro) {
      onOpen();
    } else {
      onClose();
    }
  }, [shouldShowIntro, onOpen, onClose]);

  const dismissIntro = async (): Promise<void> => {
    setIntroDismissedLocally(true);
    onClose();
    const influencerId = influencerData?.id;
    if (!influencerId) {
      return;
    }

    try {
      await influencerApi.updateProfile({ showWalletPayoutProviderIntro: false }, influencerId);
    } catch (err) {
      console.error('Failed to dismiss wallet payout intro:', err);
    }
  };

  const handleIntroSetup = async (): Promise<void> => {
    if (isIntroSetupLoading) {
      return;
    }

    setIsIntroSetupLoading(true);
    try {
      await dismissIntro();
      await onSetup?.();
    } catch (err) {
      console.error('Failed to start wallet setup:', err);
    } finally {
      setIsIntroSetupLoading(false);
    }
  };

  return {
    isIntroOpen: isOpen,
    isIntroSetupLoading,
    dismissIntro,
    handleIntroSetup,
  };
}
